import { useEffect, useRef, useState } from "react";

import { Button } from "./ui/Button";
import { formatBytes } from "./crm/attachment-format";

/**
 * Look at a file without leaving the ticket.
 *
 * The gap this fills was a specific one: an attachment on a *draft* had no
 * address at all. The only way to check that the right invoice was about to go
 * to the customer was to send the email and then click the link on the message
 * -- one step too late, every time, and the reason the wrong file gets sent.
 * So uploads are now addressable before they are attached to anything, and this
 * viewer opens either those or a file already on a message; it is handed URLs
 * rather than a message id precisely so it does not have to know which it has.
 *
 * ## Print and download, and why print is not just a link
 *
 * Both are here because the two things people do with an attachment they have
 * opened are print it and keep it, and both were previously a right-click away
 * at best. Download is an ordinary link to the route that forces one. Print
 * needs a document to print, and the browser will only print the whole page --
 * so a picture is put in an iframe of its own first, with a stylesheet that
 * fits it to the paper. A PDF is already in one, and prints itself.
 *
 * ## What has no preview
 *
 * A spreadsheet, a zip, a Word document. There is deliberately no attempt to
 * render those: it would mean either shipping a viewer per format or handing
 * the file to a third-party one, and the honest answer -- here is what it is,
 * here is the download -- takes a second to read and cannot be wrong.
 */
export interface PreviewFile {
  name: string;
  /**
   * What the server will serve it as, if it can.
   *
   * For a pending upload this is what the *browser* said at upload time, which
   * is a claim rather than a fact -- the server re-reads the bytes and may
   * disagree. That is why a failed preview falls back to the download rather
   * than being treated as an error: the type being wrong is an expected
   * outcome, not a broken page.
   */
  mimeType: string;
  sizeBytes: number | null;
  /** Where the bytes render. Null when nothing here could show them. */
  inlineUrl: string | null;
  /** Where the bytes download. Always present -- every file can be kept. */
  downloadUrl: string;
}

const IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);

/** The essence of a Content-Type, matching what the API compares. */
function essence(mimeType: string): string {
  return mimeType.split(";")[0]!.trim().toLowerCase();
}

export function isImageType(mimeType: string): boolean {
  return IMAGE_TYPES.has(essence(mimeType));
}

export function isPdfType(mimeType: string): boolean {
  return essence(mimeType) === "application/pdf";
}

/** Whether this viewer will manage to show anything but the file's name. */
export function canPreview(mimeType: string): boolean {
  return isImageType(mimeType) || isPdfType(mimeType);
}

export function AttachmentPreview({
  file,
  onClose,
}: {
  file: PreviewFile;
  onClose: () => void;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  /*
   * The server decides from the bytes, so a file whose declared type was wrong
   * arrives here as a broken preview rather than an error. Falling back to the
   * "no preview" panel keeps the download reachable, which is the thing that
   * actually matters.
   */
  const [failed, setFailed] = useState(false);

  const isImage = !failed && file.inlineUrl !== null && isImageType(file.mimeType);
  const isPdf = !failed && file.inlineUrl !== null && isPdfType(file.mimeType);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      /* Stopped, because this opens on top of the composer and of the ticket
         dialogs, and Escape should close the thing in front. */
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const print = (): void => {
    if (isPdf) {
      /*
       * Already a document, so it prints itself. Same-origin, which is what
       * makes contentWindow reachable -- and the reason the PDF response drops
       * the `sandbox` directive the image one keeps. If a browser refuses
       * anyway, opening it in a tab puts it in front of the viewer's own print
       * button rather than leaving the click doing nothing.
       */
      try {
        frame.current?.contentWindow?.print();
        return;
      } catch {
        window.open(file.inlineUrl ?? file.downloadUrl, "_blank", "noopener");
        return;
      }
    }

    if (!isImage || !file.inlineUrl) return;
    printImage(file.inlineUrl, file.name);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/70 p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={file.name}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="mx-auto flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-line bg-surface">
        <header className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-content" title={file.name}>
              {file.name}
            </p>
            <p className="text-[11px] text-faint">
              {file.mimeType}
              {file.sizeBytes ? ` · ${formatBytes(file.sizeBytes)}` : ""}
            </p>
          </div>

          {/*
            Print is offered only where there is something to print. A button
            that is present and does nothing is worse than one that was never
            drawn -- the click is spent finding that out.
          */}
          {isImage || isPdf ? (
            <Button variant="secondary" size="sm" onClick={print}>
              Print
            </Button>
          ) : null}

          <a
            href={file.downloadUrl}
            download={file.name}
            className="inline-flex h-7 items-center rounded-md border border-line bg-surface px-2.5 text-xs text-content hover:bg-surface-2"
          >
            Download
          </a>

          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            ✕
          </Button>
        </header>

        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-surface-2 p-3">
          {isImage && file.inlineUrl ? (
            <img
              src={file.inlineUrl}
              alt={file.name}
              onError={() => setFailed(true)}
              className="max-h-full max-w-full object-contain"
            />
          ) : isPdf && file.inlineUrl ? (
            <iframe
              ref={frame}
              src={file.inlineUrl}
              title={file.name}
              className="h-full w-full rounded border border-line bg-white"
            />
          ) : (
            <div className="p-8 text-center">
              <p className="text-4xl" aria-hidden>
                📎
              </p>
              <p className="mt-2 text-sm text-content">No preview for this type</p>
              <p className="mt-1 text-xs text-muted">
                Download it to open it in the application it belongs to.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Print one picture on its own sheet.
 *
 * `window.print()` prints the page, and the page here is a ticket. So the image
 * goes into an off-screen iframe of its own and that is printed instead --
 * which also gives somewhere to put the two lines of CSS that stop a screenshot
 * being printed across four pages.
 *
 * The iframe is removed after the dialog closes rather than immediately:
 * `print()` returns as soon as the dialog is dismissed in most browsers but
 * not all, and tearing the document down underneath it prints a blank sheet.
 */
function printImage(url: string, name: string): void {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";

  frame.srcdoc = `<!doctype html><title>${escapeHtml(name)}</title><style>
    html,body{margin:0;height:100%}
    img{max-width:100%;max-height:100%;object-fit:contain;display:block;margin:auto}
    @page{margin:12mm}
  </style><img src="${escapeHtml(url)}" alt="">`;

  frame.onload = () => {
    const view = frame.contentWindow;
    if (!view) return;

    /* The image inside the srcdoc has its own load to finish; printing before
       it does prints an empty box. */
    const go = (): void => {
      view.focus();
      view.print();
      window.setTimeout(() => frame.remove(), 60_000);
    };

    const image = view.document.querySelector("img");
    if (image?.complete) go();
    else image?.addEventListener("load", go, { once: true });
    image?.addEventListener("error", () => frame.remove(), { once: true });
  };

  document.body.append(frame);
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
